import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/shared/api/axios';
import type {
  CreateJobPayload,
  JobApplication,
  JobPosting,
  JobStatus,
  LinkedOfferLetterSummary,
  RecruitmentSummary,
  StageMovePayload,
  StageMoveResult,
} from './types';

const BASE = '/api/recruitment';
const OFFERS_BASE = '/api/hr-offers';

export const recruitmentKeys = {
  root: ['recruitment'] as const,
  summary: ['recruitment', 'summary'] as const,
  jobs: ['recruitment', 'jobs'] as const,
  applications: (jobId: string | null) => ['recruitment', 'applications', jobId] as const,
  application: (applicationId: string | null) => ['recruitment', 'application', applicationId] as const,
  linkedOffer: (applicationId: string | null) => ['recruitment', 'linked-offer', applicationId] as const,
};

export function useRecruitmentSummary() {
  return useQuery({
    queryKey: recruitmentKeys.summary,
    queryFn: async () => (await api.get<RecruitmentSummary>(`${BASE}/summary`)).data,
    staleTime: 30_000,
  });
}

export function useJobPostings() {
  return useQuery({
    queryKey: recruitmentKeys.jobs,
    queryFn: async () => (await api.get<JobPosting[]>(`${BASE}/jobs`)).data,
  });
}

export function useJobApplications(jobId: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.applications(jobId),
    queryFn: async () => {
      // When no specific job is selected, fetch all applications across all postings
      if (!jobId) {
        return (await api.get<JobApplication[]>(`${BASE}/applications`)).data;
      }
      return (await api.get<JobApplication[]>(`${BASE}/jobs/${jobId}/applications`)).data;
    },
    enabled: true,
  });
}

/** One application by id — backs the full-screen Application Detail page. */
export function useApplication(applicationId: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.application(applicationId),
    queryFn: async () => (await api.get<JobApplication>(`${BASE}/applications/${applicationId}`)).data,
    enabled: !!applicationId,
  });
}

export function useRecruitmentMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.root });

  const createJob = useMutation({
    mutationFn: async (payload: CreateJobPayload) => (await api.post<JobPosting>(`${BASE}/jobs`, payload)).data,
    onSuccess: refresh,
  });

  const updateJob = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; status?: JobStatus } & Partial<CreateJobPayload>) =>
      (await api.patch<JobPosting>(`${BASE}/jobs/${id}`, payload)).data,
    onSuccess: refresh,
  });

  const addApplication = useMutation({
    mutationFn: async ({
      jobId,
      resume,
      ...payload
    }: {
      jobId: string;
      candidateName: string;
      email?: string;
      phone?: string;
      coverNote?: string;
      source?: string;
      resume?: File | null;
    }) => {
      // When a resume PDF is attached we must send multipart/form-data. The
      // shared axios instance defaults to application/json, which axios v1
      // would use to serialise the FormData into JSON (dropping the file), so
      // the multipart Content-Type is set explicitly; the browser then fills
      // in the boundary.
      if (resume) {
        const form = new FormData();
        form.append('candidateName', payload.candidateName);
        if (payload.email) form.append('email', payload.email);
        if (payload.phone) form.append('phone', payload.phone);
        if (payload.coverNote) form.append('coverNote', payload.coverNote);
        if (payload.source) form.append('source', payload.source);
        form.append('resume', resume);
        // Content-Type must be unset (null) so the browser generates the
        // multipart boundary itself. Setting it to 'multipart/form-data'
        // explicitly sends a boundary-less header that the server can't parse;
        // the shared instance's application/json default would serialise the
        // FormData to JSON and drop the file. null removes the header entirely.
        return (
          await api.post<JobApplication>(`${BASE}/jobs/${jobId}/applications`, form, {
            headers: { 'Content-Type': null },
          })
        ).data;
      }
      return (await api.post<JobApplication>(`${BASE}/jobs/${jobId}/applications`, payload)).data;
    },
    onSuccess: refresh,
  });

  const screenApplication = useMutation({
    mutationFn: async (id: string) => (await api.post<JobApplication>(`${BASE}/applications/${id}/screen`)).data,
    onSuccess: (_data, id) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.application(id) });
    },
  });

  const moveStage = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & StageMovePayload) =>
      (await api.post<StageMoveResult>(`${BASE}/applications/${id}/stage`, payload)).data,
    onSuccess: (_data, variables) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.application(variables.id) });
      // The offer letter linked to this application may have just been
      // created/updated by the bridge — refresh that lookup too.
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.linkedOffer(variables.id) });
    },
  });

  return { createJob, updateJob, addApplication, screenApplication, moveStage };
}

/** The Offer Letter auto-created for an application once it reaches the
 * 'hired' stage (or null if none exists yet — e.g. application hasn't
 * reached that stage). Used by the Recruitment page to link straight to the
 * formal offer letter for hired candidates. */
export function useLinkedOfferLetter(applicationId: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.linkedOffer(applicationId),
    queryFn: async () => {
      try {
        const { data } = await api.get<LinkedOfferLetterSummary>(`${OFFERS_BASE}/by-application/${applicationId}`);
        return data;
      } catch (err: unknown) {
        // 404 just means no offer letter has been generated for this
        // application yet — a normal, expected state, not an error to surface.
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!applicationId,
  });
}

/* ── Master Data: Departments ─────────────────────────────────────────────── */

export interface MasterDepartment {
  label: string;
  value: string;
}

export const departmentKeys = {
  list: ['workspace', 'departments'] as const,
};

/** Fetch the dynamic department list from workspace org master data. */
export function useMasterDepartments() {
  return useQuery({
    queryKey: departmentKeys.list,
    queryFn: async () => {
      const { data } = await api.get<{ departments: MasterDepartment[] }>('/api/workspace/org/departments');
      return data.departments;
    },
    staleTime: 60_000,
  });
}

/** Update the full departments list (add/edit/remove) in OrgSettings. */
export function useUpdateDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (departments: MasterDepartment[]) => {
      const { data } = await api.post<{ departments: MasterDepartment[] }>('/api/workspace/org/departments', { departments });
      return data.departments;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: departmentKeys.list }),
  });
}

/* ── Master Data: Designations ────────────────────────────────────────────── */

export interface MasterDesignation {
  label: string;
  value: string;
  department?: string;
  /**
   * RBAC role this designation grants when selected in Create User — e.g.
   * "Full Stack Developer" (a job title) maps to baseRole "developer" (an
   * access level). Optional so designations saved before this field existed
   * still round-trip; Create User excludes any designation missing it rather
   * than guessing (2026-08-16 — designation and role were previously
   * conflated into a single form field).
   */
  baseRole?: string;
  active?: boolean;
  createdAt?: string;
}

export const designationKeys = {
  list: ['workspace', 'designations'] as const,
};

/** Fetch the dynamic designation list from workspace org master data. */
export function useMasterDesignations() {
  return useQuery({
    queryKey: designationKeys.list,
    queryFn: async () => {
      const { data } = await api.get<{ designations: MasterDesignation[] }>('/api/workspace/org/designations');
      return data.designations;
    },
    staleTime: 60_000,
  });
}

/** Update the full designations list (add/edit/remove) in OrgSettings. */
export function useUpdateDesignations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (designations: MasterDesignation[]) => {
      const { data } = await api.post<{ designations: MasterDesignation[] }>('/api/workspace/org/designations', { designations });
      return data.designations;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: designationKeys.list }),
  });
}

/* ── Master Data: Company Profile ─────────────────────────────────────────────
 * A singleton (not a list, unlike Departments/Designations above) — one
 * company-identity record per workspace. Added 2026-08-16 to de-duplicate
 * company name/legal name/CIN/GST/address/contact/logo, which the Letterhead
 * Branding page (frontend/src/features/hr/pages/HrBrandingPage.tsx) and
 * Payroll > Setup > Company Settings (PayrollSettingsTab.tsx) each used to
 * store a private copy of. Both now read this record; each keeps only its own
 * surface-specific fields (letterhead: artwork/signatory/socials, payroll:
 * accent color/currency/toggles). */

export interface MasterCompanyProfile {
  companyName: string;
  legalName?: string;
  cin?: string;
  gst?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
  logoUrl?: string;
}

export const companyProfileKeys = {
  detail: ['workspace', 'company-profile'] as const,
};

/** Fetch the single company-identity record shared across the workspace. */
export function useCompanyProfile() {
  return useQuery({
    queryKey: companyProfileKeys.detail,
    queryFn: async () => {
      const { data } = await api.get<{ companyProfile: MasterCompanyProfile }>('/api/workspace/org/company-profile');
      return data.companyProfile;
    },
    staleTime: 60_000,
  });
}

/** Update the company-identity record. */
export function useUpdateCompanyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (companyProfile: MasterCompanyProfile) => {
      const { data } = await api.post<{ companyProfile: MasterCompanyProfile }>('/api/workspace/org/company-profile', { companyProfile });
      return data.companyProfile;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: companyProfileKeys.detail }),
  });
}

/* ── Master Data: Salary Components (payroll earnings/deductions) ───────────
 * See docs/PAYROLL-SALARY-COMPONENTS-IMPLEMENTATION-PLAN.md — this is the
 * master-data layer that replaces the hard-coded 50/20/30 payroll formula.
 * Same OrgSettings-catalog pattern as Departments/Designations above. */

export type SalaryComponentType = 'earning' | 'deduction';
export type SalaryComponentCalcType = 'fixed_amount' | 'percent_of_base' | 'percent_of_component' | 'slab';

export interface MasterSalaryComponent {
  code: string;
  name: string;
  type: SalaryComponentType;
  calcType: SalaryComponentCalcType;
  /** Component code this is calculated against (only for calcType: percent_of_component). */
  calcRef?: string | null;
  /** Explicit evaluation order — required so percent_of_component always resolves deterministically. */
  calcOrder: number;
  /** Default % (for percent_of_*) or amount (for fixed_amount), editable per employee at assignment time. */
  defaultValue?: number;
  /** Base-salary bands for calcType: "slab" (e.g. tax slabs), inclusive minBase, sorted ascending, value is a % of base salary. */
  slabs?: { minBase: number; value: number }[];
  /** Feeds TDS calculation. */
  taxable?: boolean;
  /** Prorate by attendance/LOP days in the month. */
  proRata?: boolean;
  /** EPF/ESI/PT/TDS-style statutory components — flagged so they can't be casually deleted once used. */
  statutory?: boolean;
  /** Chart-of-Accounts code this component posts to (accounting integration). */
  glAccountCode?: string | null;
  active?: boolean;
}

export const salaryComponentKeys = {
  list: ['workspace', 'salaryComponents'] as const,
};

/** Fetch the dynamic salary component catalog from workspace org master data. */
export function useMasterSalaryComponents() {
  return useQuery({
    queryKey: salaryComponentKeys.list,
    queryFn: async () => {
      const { data } = await api.get<{ salaryComponents: MasterSalaryComponent[] }>('/api/workspace/org/salary-components');
      return data.salaryComponents;
    },
    staleTime: 60_000,
  });
}

/** Update the full salary component list (add/edit/remove) in OrgSettings. */
export function useUpdateSalaryComponents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (salaryComponents: MasterSalaryComponent[]) => {
      const { data } = await api.post<{ salaryComponents: MasterSalaryComponent[] }>(
        '/api/workspace/org/salary-components',
        { salaryComponents },
      );
      return data.salaryComponents;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: salaryComponentKeys.list }),
  });
}

/* ── Master Data: Salary Structures (bundles of salary components) ──────────
 * A structure is what an employee actually gets assigned to (Compensation
 * model's `structure_code`, see src/api-v2/hr.js). Same catalog pattern. */

export interface MasterSalaryStructure {
  code: string;
  name: string;
  description?: string;
  componentCodes: string[];
  active?: boolean;
}

export const salaryStructureKeys = {
  list: ['workspace', 'salaryStructures'] as const,
};

/** Fetch the dynamic salary structure catalog from workspace org master data. */
export function useMasterSalaryStructures() {
  return useQuery({
    queryKey: salaryStructureKeys.list,
    queryFn: async () => {
      const { data } = await api.get<{ salaryStructures: MasterSalaryStructure[] }>('/api/workspace/org/salary-structures');
      return data.salaryStructures;
    },
    staleTime: 60_000,
  });
}

/** Update the full salary structure list (add/edit/remove) in OrgSettings. */
export function useUpdateSalaryStructures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (salaryStructures: MasterSalaryStructure[]) => {
      const { data } = await api.post<{ salaryStructures: MasterSalaryStructure[] }>(
        '/api/workspace/org/salary-structures',
        { salaryStructures },
      );
      return data.salaryStructures;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: salaryStructureKeys.list }),
  });
}
